import React, { memo } from 'react';
import { Tooltip } from "@heroui/react";

interface Props {
  color: string;
  number: number;
  labels?: string[];
  sources?: any[];
  hideLabels?: boolean;
}

// Apply React.memo to avoid unnecessary re-renders
const ColoredDot: React.FC<Props> = memo(({ color, number, labels = [], hideLabels = false }) => {
  // Mapping colors to Tailwind CSS classes
  const colorClasses = {
    green: "text-status-good border-status-good bg-status-good/10",
    orange: "text-status-medium border-status-medium bg-status-medium/10",
    red: "text-status-bad border-status-bad bg-status-bad/10",
    transparent: "border-neutral-primary/30 bg-transparent",
  };

  const enhancedColorClasses = {
    green: "bg-status-good border-status-good",
    orange: "bg-status-medium border-status-medium",
    red: "bg-status-bad border-status-bad",
  };
  const enhancedTextColorClasses = {
    green: "font-medium text-status-good",
    orange: "font-medium text-status-medium",
    red: "font-medium text-status-bad",
  };

  // Ensuring that color prop is used to select the right class
  const baseColorStyle =
    (colorClasses as any)[color] ||
    "border-neutral-primary/20 bg-neutral-primary/10"; // Default to gray if color not found
  const textColorStyle =
    number > 0
      ? (enhancedTextColorClasses as any)[color] || "text-neutral-secondary-mid"
      : "text-neutral-secondary-mid";
  const dotColorStyle =
    number > 0
      ? (enhancedColorClasses as any)[color] || baseColorStyle
      : baseColorStyle;

  return (
    <div className={`flex items-center justify-center gap-1.5`}>
      {!hideLabels && <Tooltip
        content={labels && labels.join(", ")}
        containerPadding={50}
        classNames={{
          base: "max-w-[700px]",
          content:
            "max-h-[200px] top-0 overflow-y-scroll items-start justify-start p-3 font-normal leading-normal",
        }}
      >
        <div className={`h-4 w-4 rounded-full border ${dotColorStyle}`}></div>
      </Tooltip> || <div className={`h-4 w-4 rounded-full border ${dotColorStyle}`}></div>}
      <span
        className={`w-5 text-[13px] text-neutral-secondary-mid ${textColorStyle}`}
      >
        {number}
      </span>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to optimize rendering
  // Only re-render if these critical props change
  return (
    prevProps.color === nextProps.color &&
    prevProps.number === nextProps.number &&
    prevProps.hideLabels === nextProps.hideLabels &&
    // For labels, we only care about length changes if showing them
    (prevProps.hideLabels || 
      (prevProps.labels?.length === nextProps.labels?.length))
  );
});

// Add display name for better debugging
ColoredDot.displayName = 'ColoredDot';

export default ColoredDot;
